import { Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAppStore from './store/app.store';
import { IcLock } from './components/ui/Icons';
import Layout from './components/layout/Layout';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0B1220', color: '#E2E8F0', padding: 32, fontFamily: 'monospace' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#F87171', marginBottom: 12 }}>Erro na aplicação</h2>
        <pre style={{ background: '#131D31', padding: 16, borderRadius: 10, fontSize: 12, maxWidth: 700, overflowX: 'auto', color: '#FCA5A5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {this.state.error?.toString()}{'\n\n'}{this.state.error?.stack}
        </pre>
        <button onClick={() => { localStorage.clear(); window.location.href = '/login'; }} style={{ marginTop: 20, padding: '10px 24px', background: '#6C4CF5', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
          Limpar cache e ir para login
        </button>
      </div>
    );
    return this.props.children;
  }
}
import Login from './pages/Login';
import Dashboard from './pages/dashboard/Dashboard';
import CRM from './pages/crm/CRM';
import Clientes from './pages/clientes/Clientes';
import Contratos from './pages/contratos/Contratos';
import Acompanhamento from './pages/acompanhamento/Acompanhamento';
import Midias from './pages/midias/Midias';
import Trafego from './pages/trafego/Trafego';
import Chamados from './pages/chamados/Chamados';
import Financeiro from './pages/financeiro/Financeiro';
import Admin from './pages/admin/Admin';
import Reunioes from './pages/reunioes/Reunioes';
import Chat from './pages/chat/Chat';
import Conta from './pages/conta/Conta';
import SolicitacaoDemanda from './pages/SolicitacaoDemanda';
import Comercial from './pages/comercial/Comercial';
import ComercialPublico from './pages/comercial/ComercialPublico';
import Recorrentes from './pages/recorrentes/Recorrentes';
import Projetos from './pages/projetos/Projetos';
import TrocarSenhaObrigatoria from './pages/TrocarSenhaObrigatoria';
import Pauta from './pages/pauta/Pauta';
import Equipe from './pages/pauta/Equipe';
import Rotinas from './pages/pauta/Rotinas';

function Inicio() {
  const usuario = useAppStore(s => s.usuario);
  return <Navigate to={usuario?.nivel_acesso <= 1 ? '/equipe' : '/pauta'} replace />;
}

function PrivateRoute({ children }) {
  const usuarioLogado = useAppStore(s => s.usuarioLogado);
  const deveTrocarSenha = useAppStore(s => s.usuario?.deve_trocar_senha);
  if (!usuarioLogado) return <Navigate to="/login" replace />;
  return deveTrocarSenha ? <TrocarSenhaObrigatoria /> : children;
}

function GuardedRoute({ modulo, children }) {
  const usuario = useAppStore(s => s.usuario);
  if (!usuario) return <Navigate to="/login" replace />;
  const livre = ['pauta', 'conta'].includes(modulo);
  const temAcesso = livre || usuario.nivel_acesso <= 1 || (usuario.modulos || []).includes(modulo);
  if (!temAcesso) return (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <div className="mb-3 flex justify-center text-gray-400"><IcLock size={48} /></div>
      <p className="text-lg font-semibold text-gray-700">Acesso restrito</p>
      <p className="text-sm text-gray-400 mt-1">Você não tem permissão para acessar este módulo.</p>
    </div>
  );
  return children;
}

export default function App() {
  const usuarioLogado = useAppStore(s => s.usuarioLogado);
  const tema = useAppStore(s => s.tema);

  // Aplica o tema no <html> sempre que mudar
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-tema', tema || 'escuro');
  }

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Routes>
        <Route path="/login" element={usuarioLogado ? <Navigate to="/" replace /> : <Login />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Inicio />} />
          <Route path="pauta"          element={<GuardedRoute modulo="pauta">          <Pauta />        </GuardedRoute>} />
          <Route path="equipe"         element={<GuardedRoute modulo="equipe">         <Equipe />       </GuardedRoute>} />
          <Route path="rotinas"        element={<GuardedRoute modulo="rotinas">        <Rotinas />      </GuardedRoute>} />
          <Route path="dashboard"      element={<GuardedRoute modulo="dashboard">      <Dashboard />    </GuardedRoute>} />
          <Route path="crm"            element={<GuardedRoute modulo="crm">            <CRM />          </GuardedRoute>} />
          <Route path="clientes"       element={<GuardedRoute modulo="clientes">       <Clientes />     </GuardedRoute>} />
          <Route path="contratos"      element={<GuardedRoute modulo="contratos">      <Contratos />    </GuardedRoute>} />
          <Route path="acompanhamento" element={<GuardedRoute modulo="acompanhamento"> <Acompanhamento /></GuardedRoute>} />
          <Route path="midias"         element={<GuardedRoute modulo="midias">         <Midias />       </GuardedRoute>} />
          <Route path="trafego"        element={<GuardedRoute modulo="trafego">        <Trafego />      </GuardedRoute>} />
          <Route path="chamados"       element={<GuardedRoute modulo="chamados">       <Chamados />     </GuardedRoute>} />
          <Route path="recorrentes"    element={<GuardedRoute modulo="chamados">       <Recorrentes />  </GuardedRoute>} />
          <Route path="projetos"       element={<GuardedRoute modulo="projetos">       <Projetos />     </GuardedRoute>} />
          <Route path="chat"            element={<GuardedRoute modulo="chat">           <Chat />         </GuardedRoute>} />
          <Route path="reunioes"       element={<GuardedRoute modulo="reunioes">       <Reunioes />     </GuardedRoute>} />
          <Route path="financeiro"     element={<GuardedRoute modulo="financeiro">     <Financeiro />   </GuardedRoute>} />
          <Route path="admin"          element={<GuardedRoute modulo="admin">          <Admin />        </GuardedRoute>} />
          <Route path="conta"           element={<GuardedRoute modulo="conta">      <Conta />      </GuardedRoute>} />
          <Route path="comercial"       element={<GuardedRoute modulo="comercial">  <Comercial />  </GuardedRoute>} />
        </Route>
        <Route path="/c/:token" element={<ComercialPublico />} />
        <Route path="/solicitar/:slug" element={<SolicitacaoDemanda />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
